import React, { createContext, useEffect, useMemo, useState } from 'react';

import { User } from 'oidc-client-ts';

import { getUser, login, logout, onSilentRenewError, signinCallback } from 'services/auth.service';

export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSessionExpired: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isSessionExpired: false,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve()
});

// Detects whether the current URL is an OIDC redirect callback from the IdP.
// The IdP appends ?code=...&state=... to the redirect_uri after authentication.
const isOidcCallback = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') && params.has('state');
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        if (isOidcCallback()) {
          // Exchange the auth code for tokens and store in localStorage
          const loggedInUser = await signinCallback();
          setUser(loggedInUser);
          // Remove ?code=&state= from the URL without a hard reload.
          // HashRouter then renders the default hash route (/#/ → Dashboard).
          window.history.replaceState({}, document.title, '/');
        } else {
          // Normal load — restore session from localStorage if not expired
          const storedUser = await getUser();
          if (storedUser && !storedUser.expired) {
            setUser(storedUser);
          }
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    init();

    return onSilentRenewError(() => {
      setUser(null);
      setIsSessionExpired(true);
    });
  }, []);

  const contextValue = useMemo<AuthContextValue>(
    () => ({ user, isLoading, isAuthenticated: !!user && !user.expired, isSessionExpired, login, logout }),
    [user, isLoading, isSessionExpired]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
